import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Upload } from 'lucide-react';
import { toast } from 'sonner';
import { BRAZILIAN_STATES } from '@/utils/states';
import ImageCropModal from '@/components/ImageCropModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AddPlayerModal({ isOpen, onClose, onSave, initialData }) {
  const [playerType, setPlayerType] = useState('real');
  const [photoUrl, setPhotoUrl] = useState('');
  const [gameTag, setGameTag] = useState('');
  const [age, setAge] = useState('');
  const [state, setState] = useState('');
  const [uploading, setUploading] = useState(false);
  const [tempImageUrl, setTempImageUrl] = useState(null);
  const [showCropModal, setShowCropModal] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setPlayerType(initialData.type || 'real');
        setPhotoUrl(initialData.photo_url || '');
        setGameTag(initialData.game_tag || '');
        setAge(initialData.age || '');
        setState(initialData.state || '');
      } else {
        // Reset form when opening for new player
        setPlayerType('real');
        setPhotoUrl('');
        setGameTag('');
        setAge('');
        setState('');
      }
    }
  }, [isOpen, initialData]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Por favor, selecione uma imagem');
      return;
    }

    // Create local URL for cropping
    const reader = new FileReader();
    reader.onload = (event) => {
      setTempImageUrl(event.target.result);
      setShowCropModal(true);
    };
    reader.readAsDataURL(file);
    
    // Reset file input
    e.target.value = '';
  };

  const handleCroppedImage = async (croppedDataUrl) => {
    setUploading(true);

    try {
      // Convert base64 to blob
      const response = await fetch(croppedDataUrl);
      const blob = await response.blob();
      
      const formData = new FormData();
      formData.append('file', blob, 'cropped-image.jpg');

      const uploadResponse = await fetch(`${BACKEND_URL}/api/teams/upload-photo`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!uploadResponse.ok) throw new Error('Upload failed');

      const data = await uploadResponse.json();
      // Use the full image URL for display, backend returns both photo_url (filename) and image_url (full URL)
      setPhotoUrl(data.image_url || data.photo_url);
      toast.success('Foto carregada com sucesso!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Erro ao carregar foto');
    } finally {
      setUploading(false);
      setShowCropModal(false);
      setTempImageUrl(null);
    }
  };

  const handleSave = () => {
    if (playerType === 'real') {
      if (!photoUrl || !photoUrl.trim()) {
        toast.error('Por favor, carregue uma foto do jogador');
        return;
      }
      if (!gameTag.trim()) {
        toast.error('Por favor, digite a game tag');
        return;
      }
      if (!age || age < 1 || age > 100) {
        toast.error('Por favor, digite uma idade válida');
        return;
      }
      if (!state) {
        toast.error('Por favor, selecione o estado');
        return;
      }
    }

    const playerData = {
      type: playerType,
      photo_url: playerType === 'real' ? photoUrl : null,
      game_tag: playerType === 'real' ? gameTag : null,
      age: playerType === 'real' ? parseInt(age) : null,
      state: playerType === 'real' ? state : null
    };

    onSave(playerData);
  };

  const handleClose = () => {
    setTempImageUrl(null);
    setShowCropModal(false);
    onClose();
  };

  return (
    <>
      <Dialog open={isOpen && !showCropModal} onOpenChange={handleClose}>
        <DialogContent className="glass-card border-white/10 text-white max-w-md" data-testid="add-player-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold" style={{ fontFamily: 'Outfit' }}>
              Adicionar/Editar Jogador
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label className="text-white mb-2 block">Tipo</Label>
              <Tabs value={playerType} onValueChange={setPlayerType} className="w-full">
                <TabsList className="grid w-full grid-cols-2 bg-white/5">
                  <TabsTrigger 
                    value="real" 
                    className="data-[state=active]:bg-[#FF5A00] data-[state=active]:text-white"
                    data-testid="player-type-real"
                  >
                    Jogador
                  </TabsTrigger>
                  <TabsTrigger 
                    value="bot" 
                    className="data-[state=active]:bg-[#FF5A00] data-[state=active]:text-white"
                    data-testid="player-type-bot"
                  >
                    Bot
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            {playerType === 'real' && (
              <>
                <div>
                  <Label className="text-white mb-2 block">
                    Foto do Jogador <span className="text-[#FF5A00]">*</span>
                  </Label>
                  <div className="flex items-center gap-4">
                    {photoUrl && (
                      <img 
                        src={photoUrl} 
                        alt="Preview" 
                        className="w-20 h-20 rounded-full object-cover border-2 border-[#FF5A00]"
                      />
                    )}
                    <label className="cursor-pointer">
                      <div className="btn-secondary inline-flex items-center px-4 py-2">
                        <Upload className="mr-2 h-4 w-4" />
                        {uploading ? 'Carregando...' : photoUrl ? 'Alterar Foto' : 'Carregar Foto'}
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileSelect}
                        className="hidden"
                        disabled={uploading}
                        data-testid="photo-upload-input"
                      />
                    </label>
                  </div>
                  {!photoUrl && (
                    <p className="text-yellow-500 text-xs mt-1">* Foto obrigatória</p>
                  )}
                </div>

                <div>
                  <Label className="text-white mb-2 block">Game Tag</Label>
                  <Input
                    value={gameTag}
                    onChange={(e) => setGameTag(e.target.value)}
                    placeholder="Digite a game tag"
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="game-tag-input"
                  />
                </div>

                <div>
                  <Label className="text-white mb-2 block">Idade</Label>
                  <Input
                    type="number"
                    value={age}
                    onChange={(e) => setAge(e.target.value)}
                    placeholder="Digite a idade"
                    min="1"
                    max="100"
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="age-input"
                  />
                </div>

                <div>
                  <Label className="text-white mb-2 block">Estado</Label>
                  <Select value={state} onValueChange={setState}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="state-select">
                      <SelectValue placeholder="Selecione o estado" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#14151C] border-white/10 max-h-60">
                      {BRAZILIAN_STATES.map(st => (
                        <SelectItem key={st} value={st} className="text-white hover:bg-white/10">
                          {st}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {playerType === 'bot' && (
              <div className="text-center py-8">
                <p className="text-[#A1A1AA] text-lg">Nenhuma informação adicional necessária para bot</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button onClick={onClose} className="btn-secondary" data-testid="cancel-button">
              Cancelar
            </Button>
            <Button onClick={handleSave} className="btn-primary" data-testid="save-player-button">
              Salvar Jogador
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {showCropModal && tempImageUrl && (
        <ImageCropModal
          isOpen={showCropModal}
          onClose={() => {
            setShowCropModal(false);
            setTempImageUrl(null);
          }}
          onSave={handleCroppedImage}
          imageUrl={tempImageUrl}
        />
      )}
    </>
  );
}